import { Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DeleteIcon } from '@/shared/assets/icons/delete-icon';
import { DeleteIconContainer } from '@/shared/components/ui/confirm-delete/confirm-delete.styled';
import { TConfirmDeleteProps } from '@/shared/components/ui/confirm-delete/confirm-delete.types';
import { ConfirmDialog } from '@/shared/components/ui/confirm-dialog/confirm-dialog';
import { TextField } from '@/shared/components/ui/form/components/text-field/text-field';
import { Trigger } from '@/shared/components/ui/trigger/trigger';
import { usePermissions } from '@/shared/features/ability/hooks/use-permissions/use-permissions';
import { showToastError, showToastSuccess } from '@/shared/utils/toast';

export const ConfirmDelete: React.FC<TConfirmDeleteProps> = ({
  component = 'Button',
  componentSize = 'extraSmall',
  onClose,
  componentLabel,
  disabled,
  tooltip,
  itemCount,
  deleteWarning,
  optionalDeleteAction,
  recoverNote,
  deleteMutation,
  onSuccess,
  onClick,
  successMessage,
  errorMessage,
  permission,
}) => {
  const { t } = useTranslation();
  const [deleteMessage, setDeleteMessage] = useState<string>('');
  const _successMessage =
    successMessage ??
    t('ui:confirm_delete_modal.delete_success', {
      count: itemCount,
    });
  const _errorMessage =
    errorMessage ??
    t('ui:confirm_delete_modal.delete_error', {
      count: itemCount,
    });
  const primaryButtonLabel = t('common:delete');
  const primaryButtonVariant = 'alert';
  const label = componentLabel ?? t('common:delete');
  const deleteTranslated = t('common:delete');
  const hasPermission = usePermissions(permission);
  const shouldRunOptionalAction = optionalDeleteAction?.checked;

  const onDelete = async () => {
    await deleteMutation();

    if (!shouldRunOptionalAction || !optionalDeleteAction) {
      return;
    }

    try {
      await optionalDeleteAction.mutation();
      showToastSuccess(optionalDeleteAction.successMessage);
    } catch {
      showToastError(optionalDeleteAction.errorMessage);
    }
  };

  const renderHeadline = () => {
    return (
      <>
        <DeleteIconContainer>
          <DeleteIcon />
        </DeleteIconContainer>
        <Typography variant="h2" textAlign="center">
          {t('ui:confirm_delete_modal.confirm_delete', {
            count: itemCount,
          })}
        </Typography>
      </>
    );
  };

  const renderAdditionalContent = () => {
    return (
      <>
        {itemCount > 1 && (
          <Typography variant="p5" color="textSecondary">
            <Trans
              t={t}
              i18nKey="ui:confirm_delete_modal.delete_count"
              components={{ bold: <strong /> }}
              values={{ count: itemCount }}
            />
          </Typography>
        )}
        {deleteWarning && <Typography variant="p4">{deleteWarning}</Typography>}
        {optionalDeleteAction && (
          <Stack gap="0.50rem" width="100%">
            {optionalDeleteAction.details}
          </Stack>
        )}
        {recoverNote && (
          <Typography variant="p5" textAlign="center">
            {recoverNote}
          </Typography>
        )}
        <TextField
          fullWidth
          placeholder={t('ui:type_delete_to_delete_asset', {
            value: deleteTranslated,
          })}
          size="small"
          value={deleteMessage}
          onChange={(e) => setDeleteMessage(e.target.value)}
        />
      </>
    );
  };

  const renderTriggerComponent = () => {
    return (
      <Trigger
        component={component}
        tooltip={tooltip}
        icon={<DeleteIcon />}
        buttonVariant="alertSecondary"
        menuItemVariant="alert"
        disabled={disabled}
        buttonSize={componentSize}
        onClick={onClick}
        label={label}
      />
    );
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <ConfirmDialog
      triggerComponent={renderTriggerComponent()}
      headline={renderHeadline()}
      additionalContent={renderAdditionalContent()}
      successMessage={_successMessage}
      errorMessage={_errorMessage}
      primaryButtonLabel={primaryButtonLabel}
      primaryButtonVariant={primaryButtonVariant}
      onSuccess={onSuccess}
      onClose={onClose}
      disabled={disabled}
      disableConfirm={deleteMessage !== deleteTranslated}
      mutation={onDelete}
    />
  );
};
